import { useSyncExternalStore } from "react";
import { MultiSelectDropdown } from "@carbon6/c6-frontend-design";
import { FilterBar, FilterSearchInput, type FilterBarChip } from "@/shared/ui/filter";
import { formatDatasetFormat } from "../../feature/datasetFormatters";
import {
  EMPTY_CANDIDATE_FILTERS,
  type CandidateFacetAxis,
  type CandidateFilters,
} from "../../feature/candidateFilters";
import type { CandidateTableSource } from "../CandidateTableSource";

const AXES: readonly { key: CandidateFacetAxis; label: string }[] = [
  { key: "formats", label: "포맷" },
  { key: "rootCodes", label: "루트" },
  { key: "parentFolders", label: "부모 폴더" },
];

type CandidateFilterBarProps = {
  filters: CandidateFilters;
  source: CandidateTableSource;
  onChange: (filters: CandidateFilters) => void;
};

/** Registry의 FacetControl과 같은 c6 다중 선택, 조건 칩, 초기화 구성. */
export function CandidateFilterBar({ filters, source, onChange }: CandidateFilterBarProps) {
  useSyncExternalStore(source.subscribe, source.getSnapshot);
  const options = source.getFilterOptions();
  const chips: FilterBarChip[] = [];
  if (filters.search) {
    chips.push({
      key: "search",
      label: `검색: ${filters.search}`,
      removeLabel: "검색 해제",
      onRemove: () => onChange({ ...filters, search: "" }),
    });
  }
  for (const { key, label } of AXES) {
    for (const value of filters[key]) {
      const display =
        key === "formats"
          ? formatDatasetFormat(value as (typeof filters.formats)[number], null)
          : value;
      chips.push({
        key: `${key}:${value}`,
        label: `${label}: ${display}`,
        removeLabel: `${label} ${display} 해제`,
        onRemove: () =>
          onChange({ ...filters, [key]: filters[key].filter((item) => item !== value) }),
      });
    }
  }
  return (
    <FilterBar
      ariaLabel="스캔 결과 필터"
      chips={chips}
      search={
        <FilterSearchInput
          ariaLabel="스캔 결과 검색"
          placeholder="원본 주소로 검색해 보세요."
          value={filters.search}
          onCommit={(search) => onChange({ ...filters, search })}
        />
      }
      onClearAll={() => onChange(EMPTY_CANDIDATE_FILTERS)}
    >
      {AXES.map(({ key, label }) => (
        <MultiSelectDropdown
          key={key}
          mode="facet"
          label={label}
          options={options[key].map((value) => ({
            value,
            label:
              key === "formats"
                ? formatDatasetFormat(value as (typeof filters.formats)[number], null)
                : value,
          }))}
          selectedValues={filters[key]}
          closeOnSelect={false}
          searchable={options[key].length >= 8}
          searchPlaceholder="검색"
          clearSelectionLabel="선택 해제"
          onSelectionChange={(values) => onChange({ ...filters, [key]: values })}
        />
      ))}
    </FilterBar>
  );
}
